import { FC } from 'react';

import ParsedDataGrid from '@components/parsed-data-grid/ParsedDataGrid';
import { UploadStep } from '@features/group-management/hooks/useGroupFileUpload';
import UploadStatusMessage from '@features/member-management/components/UploadStatusMessage';
import ModalV2 from '@layouts/modal/Modal.v2';

interface GroupFileUploadResultModalProps {
  isOpen: boolean;
  step: UploadStep;
  fileName: string;
  headers: string[];
  displayRows: Record<string, any>[];
  rowErrorsById: Record<string, string[]>;
  totalMembers: number;
  membersWithErrors: number;
  membersWithoutErrors: number;
  warningCount: number;
  primaryButtonLabel: string | undefined;
  isPrimaryButtonDisabled: boolean;
  isApiLoading: boolean;
  onClose: () => void;
  onNextClick: () => void;
  onProceedClick: () => void;
}

const GroupFileUploadResultModal: FC<GroupFileUploadResultModalProps> = ({
  isOpen,
  step,
  fileName,
  headers,
  displayRows,
  rowErrorsById,
  totalMembers,
  membersWithErrors,
  membersWithoutErrors,
  warningCount,
  primaryButtonLabel,
  isPrimaryButtonDisabled,
  isApiLoading,
  onClose,
  onNextClick,
  onProceedClick
}) => {
  if (!isOpen) return null;

  const modalTitle = fileName || 'File Preview';
  const modalDescription =
    'Here is the preview of the file that you have uploaded.';

  const renderErrorPreviewStep = () => (
    <div className="flex flex-col gap-y-2">
      <div className="align-middle text-sm font-semibold leading-[100%] text-[#344054]">
        A total of {membersWithoutErrors}/{totalMembers} members will be added.
        {membersWithErrors > 0 &&
          ` The remaining ${membersWithErrors} contain errors and must be uploaded separately.`}
      </div>
      <ParsedDataGrid
        headers={headers}
        rows={displayRows}
        rowErrorsById={rowErrorsById}
        className="mt-4"
      />
    </div>
  );

  const renderWarningPreviewStep = () => (
    <div className="flex flex-col gap-y-2">
      <div className="align-middle text-sm font-semibold leading-[100%] text-[#344054]">
        {warningCount} record(s) have warnings. Review and click Proceed to
        continue.
      </div>
      <ParsedDataGrid
        headers={headers}
        rows={displayRows}
        rowErrorsById={rowErrorsById}
        issueType="warning"
        className="mt-4"
      />
    </div>
  );

  const renderContent = () => {
    switch (step) {
      case 'error_preview':
        return renderErrorPreviewStep();
      case 'warning_preview':
        return renderWarningPreviewStep();
      default:
        return null;
    }
  };

  const renderInfoText = () => (
    <UploadStatusMessage step={step} membersWithErrors={membersWithErrors} />
  );

  return (
    <ModalV2
      title={modalTitle}
      description={modalDescription}
      onClose={onClose}
      className="w-225 min-w-125"
      primaryButtonLabel={primaryButtonLabel}
      primaryButtonClick={
        step === 'warning_preview' ? onProceedClick : onNextClick
      }
      primaryButtonDisabled={isPrimaryButtonDisabled}
      primaryButtonLoading={isApiLoading}
      secondaryButtonLabel="Cancel"
      secondaryButtonClick={onClose}
      secondaryButtonDisabled={isApiLoading}
      isClickOutsideRequired={false}
      infoText={renderInfoText()}
    >
      {renderContent()}
    </ModalV2>
  );
};

export default GroupFileUploadResultModal;
